import type { Exklusivtermin } from './termine';
import { alsTag } from '../datum';
import { alsEuro } from '../betrag';

/**
 * Ein Exklusivtermin als lesbarer Satz — Datum, Ort und die vereinbarte
 * Pauschale.
 *
 * DIE ZONE STEHT FEST und wird NICHT aus dem Browser genommen: FRS-01 macht
 * Europe/Berlin zur maßgeblichen Zone, und ein Rechner mit anderer Einstellung
 * zeigte sonst ein anderes Datum als der Kollege am Telefon. Ein Training um
 * 00:30 Berliner Zeit stand in einem UTC-Browser am Vortag. Die Hausregel
 * steht in `start/Startseite.tsx`; hier stand sie nicht, und das war ein
 * Versehen.
 *
 * Der Preis gehört daneben, weil er die Folge der Wahl ist: Nach PRD-46 IST
 * die vereinbarte Pauschale der Rechnungsbetrag.
 */
export function terminname(id: string, termine: readonly Exklusivtermin[]): string {
  const t = termine.find((x) => x.id === id);
  if (t === undefined) return id;
  const tag = new Date(t.beginn).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });
  const ort = t.ort === null ? '' : ` · ${t.ort}`;
  const preis =
    t.pauschale === null
      ? ' · keine Pauschale vereinbart'
      : ` · ${alsEuro(t.pauschale)} ${t.istBrutto ? 'brutto' : 'netto'} vereinbart`;
  /*
   * Der ZUSTAND gehört dazu. Die Auskunft liefert auch vergangene und
   * durchgeführte Termine (sie ist nach Beginn absteigend sortiert); ohne
   * diesen Zusatz stünde ein abgeschlossenes Training im Wähler wie ein
   * geplantes.
   */
  const zustand = t.status === 'geplant' || t.status === 'buchbar' ? '' : ` · ${t.status}`;
  return `${tag}${ort}${preis}${zustand}`;
}

/**
 * Der Terminbezug einer Auftragsposition — ohne je eine Kennung zu zeigen.
 *
 * ============================================================================
 * DER BEFUND VOM 15.09.2026
 * ============================================================================
 *
 * In der Auftragsakte stand in der Spalte „Bezug" die Datenbankkennung:
 *
 *     66666666-1111-4111-8111-111111111111
 *
 * Bei ALLEN 95 Terminpositionen des Bestands. Zwei Ursachen kamen zusammen:
 * `terminname` gibt die Kennung zurück, wenn der Termin nicht in der geladenen
 * Liste steht — und die Liste (`exklusiveDesKunden`) findet nur Termine mit
 * einer Exklusivvereinbarung zum Kunden.
 *
 * Ein Rückfall, der einen Datenbankschlüssel auf den Bildschirm schreibt, ist
 * keiner. Was der Anwender braucht, steht ohnehin in derselben Zeile: der
 * Leistungszeitpunkt, den Migration 0207 dort führt.
 */
export function terminbezug(
  position: {
    readonly bezugId: string | null;
    readonly bezugVon: string | null;
  },
  termine: readonly Exklusivtermin[],
): string {
  if (position.bezugId === null) return 'Termin offen';

  const gefunden = termine.find((t) => t.id === position.bezugId);
  if (gefunden !== undefined) return terminname(position.bezugId, termine);

  /*
   * Der Termin ist nicht geladen — aber sein Tag steht an der Zeile
   * (`bezugVon` kommt aus `fach.auftragsposition_leistungszeitraum`, 0207).
   * Er sagt dem Anwender genau das, wonach er sucht.
   */
  const tag = alsTag(position.bezugVon);
  return tag === null ? 'Termin vereinbart' : `Termin am ${tag}`;
}
